import { treatmentFlagValues, TreatmentName } from 'constants/treatments'
import useIsRolloutPlanSettled from 'hooks/treatments/useIsRolloutPlanSettled'
import { isRolloutPlanSettled } from 'lib/splitRolloutPlan'
import { useEffect, useState } from 'react'
import {
  getUserTreatment,
  isSplitAnalyticsConsentGranted,
  isUserSplitReady,
  isUserSplitTimedOut,
} from 'utils/treatment'

/**
 * This hook returns the valid value for a specified treatment for authenticated users.
 * It is specifically designed to work with user traffic type and supports individual
 * targeting rules in Split.io. Unlike useGetTreatment (which handles anonymous traffic),
 * this hook can handle individually targeted treatments where specific users are given
 * specific treatment values.
 *
 * As it takes some time while initializing split client, so until the split client is ready
 * we keep calling the getTreatment function after every 200ms time interval until we get
 * valid value for a particular treatment instead of 'control'.
 *
 * Key differences from useGetTreatment:
 * - Uses user traffic type (authenticated users) vs anonymous traffic type
 * - Supports individual targeting rules where specific users get specific treatments
 * - Uses getUserTreatment internally which is configured with user-specific Split client
 *
 * @param treatmentName
 * Required parameter for this hook, pass in the split treatment name.
 *
 * @param allowToFetchTreatment
 * A optional parameter to control when the getTreatment function should be called.
 *
 * @param attributes
 * Optional Split.io attributes for rule-based targeting (e.g. profileCode).
 *
 * @returns returns the treatment value for the specified treatment
 */
const useGetUserTreatment = (
  name: TreatmentName,
  allowToFetchTreatment = true,
  attributes?: SplitIO.Attributes
): string => {
  const attributesDependency = [...Object.values(attributes ?? {})]
  const rolloutPlanSettled = useIsRolloutPlanSettled()
  const [treatmentValue, setTreatmentValue] = useState<string>(
    allowToFetchTreatment
      ? getUserTreatment(name, attributes)
      : treatmentFlagValues.CONTROL
  )

  const setTreatmentFromSplit = (name: TreatmentName) => {
    const updatedTreatmentValue = getUserTreatment(name, attributes)
    setTreatmentValue(updatedTreatmentValue)
  }

  /* 
    TODO: Need to find a better way to get the valid value for a treatment instead of
    keep on calling getTreatment function until the Split client is initialized. Check
    with Split.io react SDK (https://github.com/splitio/react-client). This can be used
    later on so that we don't have to handle the split IO implementation ourselves.
  */

  useEffect(() => {
    let treatmentIntervalHandle

    if (
      allowToFetchTreatment &&
      treatmentValue === treatmentFlagValues.CONTROL
    ) {
      if (
        isUserSplitReady() ||
        isUserSplitTimedOut() ||
        (rolloutPlanSettled && !isSplitAnalyticsConsentGranted())
      ) {
        setTreatmentFromSplit(name)
      } else {
        treatmentIntervalHandle = setInterval(() => {
          if (
            isUserSplitReady() ||
            isUserSplitTimedOut() ||
            (isRolloutPlanSettled() && !isSplitAnalyticsConsentGranted())
          ) {
            setTreatmentFromSplit(name)
            clearInterval(treatmentIntervalHandle)
          }
        }, 100)
      }
    }

    return () => clearInterval(treatmentIntervalHandle)
  }, [allowToFetchTreatment, rolloutPlanSettled])

  useEffect(() => {
    if (
      allowToFetchTreatment &&
      isUserSplitReady() &&
      treatmentValue !== treatmentFlagValues.CONTROL
    ) {
      setTreatmentFromSplit(name)
    }
  }, attributesDependency)

  // Consent-denied + settled: return the live released value in the same render
  // that settlement flips, so consumers do not act on stale CONTROL for up to
  // one poll interval (100ms).
  if (
    allowToFetchTreatment &&
    treatmentValue === treatmentFlagValues.CONTROL &&
    rolloutPlanSettled &&
    !isSplitAnalyticsConsentGranted()
  ) {
    return getUserTreatment(name, attributes)
  }

  return treatmentValue
}

export default useGetUserTreatment
